import React, { useState, useEffect } from 'react';
import { Checkbox, ColorPicker, Typography, Select, Tooltip, Divider } from 'antd';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { BarChartOutlined, LineChartOutlined } from '@ant-design/icons';
import Icon from '@mdi/react';
import { mdiDrag } from '@mdi/js';

const MetricsList = ({ widget }) => {
  const { metricsList, chartConfig, setChartConfig, changeMetricsDesign } = widget;
  const [metrics, setMetrics] = useState({});
  const [checkedKeys, setCheckedKeys] = useState([]);

  useEffect(() => {
    if (metricsList) {
      const order = chartConfig?.metricsOrder || Object.keys(metricsList);
      const init = Object.fromEntries(
        order
          .map((key) => {
            const base = metricsList[key];
            const saved = chartConfig?.metrics?.[key];
            if (!base) return null;

            return [
              key,
              {
                color: saved?.color || base.color,
                name: base.name,
                show: saved?.show ?? base.show ?? false,
                type: saved?.type || base.type || 'bar',
              },
            ];
          })
          .filter(Boolean)
      );

      setMetrics(init);
      setCheckedKeys(Object.keys(init).filter((k) => init[k].show));
    }
  }, [metricsList, chartConfig?.metrics]);

  const updateChart = (updatedMetrics) => {
    setChartConfig({
      ...chartConfig,
      metrics: updatedMetrics,
      metricsOrder: Object.keys(updatedMetrics),
    });
  };

  const handleCheck = (vals) => {
    setCheckedKeys(vals);
    const updated = Object.fromEntries(
      Object.entries(metrics).map(([k, v]) => [k, { ...v, show: vals.includes(k) }])
    );
    setMetrics(updated);
    updateChart(updated);
  };

  const handleTypeChange = (key, type) => {
    const updated = { ...metrics, [key]: { ...metrics[key], type } };
    setMetrics(updated);
    updateChart(updated);
  };

  const handleColorChange = (key, color) => {
    const updated = { ...metrics, [key]: { ...metrics[key], color } };
    setMetrics(updated);
    updateChart(updated);
  };

  const handleCheckAll = (e) => {
    const checked = e.target.checked;
    const newKeys = checked ? Object.keys(metrics) : [];
    setCheckedKeys(newKeys);
    const updated = Object.fromEntries(
      Object.entries(metrics).map(([k, v]) => [k, { ...v, show: checked }])
    );
    setMetrics(updated);
    updateChart(updated);
  };

  if (!metricsList || chartConfig.selected_metrics) return null;

  return (
    <div>
      <Divider />
      <div className='flex items-center justify-between'>
        <Tooltip title='Check All'>
          <Checkbox
            indeterminate={
              checkedKeys.length > 0 && checkedKeys.length < Object.keys(metrics).length
            }
            checked={checkedKeys.length === Object.keys(metrics).length}
            onChange={handleCheckAll}
            className='font-normal text-xs'
          >
            <Typography.Text className='font-semibold text-sm text-gray-700'>
              Metrics
            </Typography.Text>
          </Checkbox>
        </Tooltip>
      </div>

      <div className=' overflow-y-auto pr-2'>
        <DragDropContext
          onDragEnd={({ source, destination }) => {
            if (!destination) return;
            const keys = Object.keys(metrics);
            const reordered = [...keys];
            const [removed] = reordered.splice(source.index, 1);
            reordered.splice(destination.index, 0, removed);
            const reorderedMetrics = Object.fromEntries(reordered.map((k) => [k, metrics[k]]));
            setMetrics(reorderedMetrics);
            updateChart(reorderedMetrics);
          }}
        >
          <Droppable droppableId='metrics-droppable'>
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {Object.keys(metrics).map((key, index) => (
                  <Draggable key={key} draggableId={key} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className='flex items-center gap-2 my-2 rounded'
                      >
                        <Tooltip title='Drag to reorder'>
                          <div
                            {...provided.dragHandleProps}
                            className={`cursor-move text-gray-400 hover:text-gray-600 ${
                              snapshot.isDragging ? 'text-orange' : ''
                            }`}
                          >
                            <Icon path={mdiDrag} size={0.8} />
                          </div>
                        </Tooltip>

                        <Checkbox
                          value={key}
                          checked={checkedKeys.includes(key)}
                          onChange={(e) => {
                            const updated = e.target.checked
                              ? [...checkedKeys, key]
                              : checkedKeys.filter((k) => k !== key);
                            handleCheck(updated);
                          }}
                        />
                        {changeMetricsDesign && (
                          <ColorPicker
                            size='small'
                            value={metrics[key].color}
                            onChange={(color) => handleColorChange(key, color.toHexString())}
                          />
                        )}
                        <Typography className='font-normal text-sm'>{metrics[key].name}</Typography>
                        {changeMetricsDesign && (
                          <Select
                            className='!h-7'
                            showSearch
                            size='small'
                            value={metrics[key].type}
                            onChange={(v) => handleTypeChange(key, v)}
                            options={[
                              {
                                value: 'line',
                                label: (
                                  <span>
                                    <LineChartOutlined style={{ marginRight: 8 }} />
                                    Line
                                  </span>
                                ),
                              },
                              {
                                value: 'bar',
                                label: (
                                  <span>
                                    <BarChartOutlined style={{ marginRight: 8 }} />
                                    Bar
                                  </span>
                                ),
                              },
                            ]}
                          />
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </div>
  );
};

export default MetricsList;
